import SendEmail from "assets/img/svg/sendEmail";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import type { IChannel } from "containers/Channels/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { generateAnalyticsConsolidatedSalesReport, resetAnalyticsConsolidatedSales } from "store/analyticsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { AnalyticsFilters } from "types";

import type { AnalyticsRefreshContext } from "../Dashboard";
import type { StoredPreferences } from "../Dashboard/utils";
import { hydrateDates } from "../Dashboard/utils";
import { Layout } from "../Page";
import AnalyticsRefreshButton from "../RefreshButton";
import type { IConsolidatedSales as IConsolidatedSales_ } from "./Content";
import Content from "./Content";

export interface IConsolidatedSales extends IConsolidatedSales_ {
    isGeneratingSalesReport?: boolean;
    refreshEmailGenerationStatus: () => void;
}

function ConsolidatedSales({
    tabsContainer,
    isGeneratingSalesReport,
    refreshEmailGenerationStatus,
    periodsDateRange,
    ...props
}: IConsolidatedSales & { tabsContainer: ReactNode }) {
    const dispatch = useAppDispatch();
    const { analyticsConsolidatedSalesReportStatus } = useAppSelector((app) => app.analytics);
    const { t } = useTranslation();
    const { getCompanySettings, companyInfos } = useCompanySettings();

    const { channels: unorderedChannels } = useAppSelector((state) => state.channels);

    const channels = useMemo<IChannel[]>(() => {
        const channelsByKind = unorderedChannels.reduce<Record<string | number, IChannel[]>>((acc, channel) => {
            if (!acc[channel.kind]) {
                acc[channel.kind] = [];
            }

            if (!channel.isDeleted) {
                acc[channel.kind].push(channel);
            }
            return acc;
        }, {});

        return Object.keys(channelsByKind)
            .sort((a, b) => Number(a) - Number(b))
            .map((k) => Object.values(channelsByKind[k]))
            .flat(1);
    }, [unorderedChannels]);

    const channelIds = useMemo(() => {
        return channels ? [...channels.map((c) => c.id), "all"] : [];
    }, [channels]);

    const storedPreferences = useMemo(
        () => getCompanySettings("analytics_layout") as StoredPreferences,
        [getCompanySettings],
    );

    const { chosenDates: storedDates } = storedPreferences?.storedFilters?.["consolidated-sales"] || {};

    const [filters, setFilters] = useState<AnalyticsFilters>({
        dates: hydrateDates(storedDates) || periodsDateRange,
        channelIds,
    });

    useEffect(() => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            channelIds,
        }));
    }, [channelIds]);

    const handleFiltersChange = useRef((updatedFilters: AnalyticsFilters) => {
        setFilters(updatedFilters);
    });

    const userHasRequestedReport = useRef(false);
    useEffect(() => {
        if (isGeneratingSalesReport) {
            userHasRequestedReport.current = true;
        }
    }, [isGeneratingSalesReport]);

    const pollEmailGenerationStatusInterval = useRef<number>();

    useEffect(() => {
        if (pollEmailGenerationStatusInterval.current) {
            clearInterval(pollEmailGenerationStatusInterval.current);
        }

        if (isGeneratingSalesReport) {
            pollEmailGenerationStatusInterval.current = window.setInterval(() => {
                refreshEmailGenerationStatus();
            }, 5 * 1000);
        }

        return () => {
            if (pollEmailGenerationStatusInterval.current) {
                clearInterval(pollEmailGenerationStatusInterval.current);
            }
        };
    }, [isGeneratingSalesReport, refreshEmailGenerationStatus]);

    useEffect(() => {
        if (userHasRequestedReport.current && !isGeneratingSalesReport) {
            userHasRequestedReport.current = false;
            toast.success(`${t("Analytics.ConsolidatedSales.ReportSuccessToaster")}`);
        }
    }, [isGeneratingSalesReport, t]);

    const refreshContext: AnalyticsRefreshContext = useMemo(
        () => getCompanySettings("analytics_refresh_context"),
        [companyInfos],
    );

    useEffect(() => {
        if (refreshContext?.needsRecompute) {
            dispatch(resetAnalyticsConsolidatedSales());
        }
    }, [refreshContext?.needsRecompute, dispatch]);

    useEffect(() => {
        if (analyticsConsolidatedSalesReportStatus === "succeeded") {
            refreshEmailGenerationStatus();
        }
    }, [analyticsConsolidatedSalesReportStatus, refreshEmailGenerationStatus]);

    return (
        <Layout
            content={
                channels?.length ? (
                    <Content
                        {...props}
                        periodsDateRange={periodsDateRange}
                        storedPreferences={storedPreferences}
                        filters={filters}
                        onFiltersChange={handleFiltersChange.current}
                        channels={channels}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center size-full">
                        <Spinner />
                    </div>
                )
            }
            tabsContainer={tabsContainer}
            header={
                <div className="flex items-center gap-2">
                    <AnalyticsRefreshButton />
                    <StyledButton
                        buttonType="primary"
                        disabled={isGeneratingSalesReport === undefined || isGeneratingSalesReport}
                        onClick={() => {
                            if (!filters || !filters.dates || !filters.channelIds?.length) {
                                return;
                            }

                            dispatch(generateAnalyticsConsolidatedSalesReport(filters));
                        }}
                    >
                        {isGeneratingSalesReport ? (
                            <span>{t("Analytics.ConsolidatedSales.ReportIsGenerating")}</span>
                        ) : (
                            <span>{t("Analytics.ConsolidatedSales.RequestReport")}</span>
                        )}
                        <SendEmail />
                    </StyledButton>
                </div>
            }
        />
    );
}

export default ConsolidatedSales;
